import {
  useTable,
  CellProps,
  useSortBy,
  useFilters,
  HeaderGroup,
  TableInstance,
  Row,
} from 'react-table';
import { round } from 'lodash';
import { toast } from 'react-toastify';
import { Table } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCopyToClipboard } from 'usehooks-ts';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import Alert from '../../../ui/Alert/Alert';
import IconMaterial from '../../../ui/IconMaterial/IconMaterial';
import TilesSummaryReport from '../TilesSummaryReport/TilesSummaryReport';
import LoadingErrorBoundaryRTK from '../../../ui/LoadingErrorBoundaryRTK/LoadingErrorBoundaryRTK';
import DownloadSummaryReportButton from '../DownloadSummaryReportButton/DownloadSummaryReportButton';

import {
  TABLE_COLUMNS,
  TABLE_CELL_STYLES,
  TABLE_HEADER_STYLES,
  TABLE_COLUMN_ACCESSORS,
  TABLE_AVERAGE_CELL_STYLES,
} from './TableSummaryReport.constants';

import { SearchInput } from '@praktio/common-ui';
import { RootState } from '../../../../ducks/store';
import { LearnerFilter } from '../GeneralReport.enum';
import { hasMultipleSelectedCourses } from '../../../../utils/reports';
import { CourseName, Learner } from './TableSummaryReport.types';
import { renderPercentageCell } from './TableSummaryReport.utils';
import { useGetCourseNamesQuery } from '../../../../services/courses';
import { useGetTeamsSlice } from '../../../../hooks/useGetTeamsSlice';
import { ToastifyNoDefaultStyling } from '../../../../utils/constants';
import { useLearnerCourseProgress } from '../../../../hooks/useLearnerCourseProgress';
import { useColumnWidths } from '../../../../hooks/useColumnWidths';
import { useVirtualizedRows } from '../../../../hooks/useVirtualizedRows';

import './TableSummaryReport.scss';
import 'react-toastify/dist/ReactToastify.css';

interface Props {
  reportData?: IReportData;
}

const TableSummaryReport = ({ reportData }: Props) => {
  const [columns, setColumns] = useState(TABLE_COLUMNS);
  const [learnerFilter, setLearnerFilter] = useState(LearnerFilter.All);
  const [originalUnfilteredRows, setOriginalUnfilteredRows] = useState<Row<Learner>[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // ===========================================================================
  // Redux
  // ===========================================================================

  const { activeTeams, activeDeselectedCourses } = useSelector((state: RootState) => ({
    activeTeams: state.teamsSlice.activeTeams,
    activeDeselectedCourses: state.teamsSlice.activeDeselectedCourses,
  }));

  // ===========================================================================
  // RTK Query
  // ===========================================================================

  const {
    data: courses,
    error,
    isLoading,
    isFetching,
  } = useGetCourseNamesQuery({ teamIds: activeTeams });

  const hasSingleSelectedCourse = !hasMultipleSelectedCourses(courses, activeDeselectedCourses);

  // ===========================================================================
  // Hooks
  // ===========================================================================

  const {
    totalLearnerCount,
    formattedLearners,
    learnersWhoHaventStartedCount,
    learnersWhoHaveCompletedCount,
    learnersWhoHaventCompletedCount,
  } = useLearnerCourseProgress(reportData);

  const {
    rows,
    footerGroups,
    headerGroups,
    state: { filters },
    setFilter,
    prepareRow,
    toggleSortBy,
    getTableProps,
    getTableBodyProps,
  }: TableInstance<Learner> = useTable(
    {
      columns,
      data: formattedLearners,
      filterTypes: {
        // Custom filter for fuzzy text search
        fuzzyText: (rows, id, filterValue) => {
          return rows.filter((row) => {
            const name = row.values.name?.toLowerCase() || '';
            const email = row.values.email?.toLowerCase() || '';
            const query = filterValue.toLowerCase();

            return name.includes(query) || email.includes(query);
          });
        },
      },
      initialState: {
        sortBy: [
          {
            id: 'name',
            desc: false,
          },
        ],
        hiddenColumns: hasSingleSelectedCourse ? ['totalPercentageComplete'] : [],
      },
    },
    useFilters,
    useSortBy,
  );

  const [_, copyToClipboard] = useCopyToClipboard();
  const { noCoursesSelected } = useGetTeamsSlice();

  // ===========================================================================
  // Virtualization
  // ===========================================================================

  const {
    containerRef,
    visibleRows,
    topPadding,
    bottomPadding,
    handleScroll: handleTableScroll,
  } = useVirtualizedRows(rows);

  // Column-width stabilization for virtualized rows — see useColumnWidths JSDoc.
  const { widestValues } = useColumnWidths(rows, columns, { containerRef });

  // ===========================================================================
  // Other
  // ===========================================================================

  const hasFilters = filters.length > 0;
  const coursesIsLoadingOrFetching = isLoading || isFetching;

  const toastifyDefaultProps = {
    ...ToastifyNoDefaultStyling,
    autoClose: 1000,
  };

  // ===========================================================================
  // Functions
  // ===========================================================================

  const customSortByToggle = (column: HeaderGroup<Learner>) => ({
    onClick: () => {
      if (column.isSorted) {
        toggleSortBy(column.id, !column.isSortedDesc);
        return;
      }

      toggleSortBy(column.id, false);
    },
  });

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleCopyClick = async (filter: LearnerFilter) => {
    try {
      const listOfEmails: string = originalUnfilteredRows
        .filter(({ original }) => {
          switch (filter) {
            case LearnerFilter.HaventStarted:
              return original.totalPercentageComplete === 0;
            case LearnerFilter.HaventCompleted:
              return (
                original.totalPercentageComplete != null &&
                original.totalPercentageComplete > 0 &&
                original.totalPercentageComplete < 100
              );
            case LearnerFilter.Completed:
              return original.totalPercentageComplete === 100;
            default:
              return true;
          }
        })
        .map(({ original }: any) => original.email)
        .join('; '); // Eg. "test@email; test2@email"

      await copyToClipboard(listOfEmails);

      toast.success(
        <Alert heading="Copied to clipboard" variant="success" />,
        toastifyDefaultProps,
      );
    } catch (error) {
      toast.success(
        <Alert heading="Failed to copy to clipboard" variant="danger" />,
        toastifyDefaultProps,
      );
    }
  };

  const handleFilterClick = (filter: LearnerFilter) => {
    setLearnerFilter(filter);
  };

  const handleClearFilter = () => {
    setLearnerFilter(LearnerFilter.All);
    setFilter(TABLE_COLUMN_ACCESSORS.TOTAL_PERCENTAGE_COMPLETE, undefined);
  };

  const handleSearch = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(value);
    setFilter(TABLE_COLUMN_ACCESSORS.NAME, value);
  };

  const handleClearSearchFilter = () => {
    setSearchQuery('');
    setFilter(TABLE_COLUMN_ACCESSORS.NAME, undefined);
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  // Store the pre-filtered rows (original list) in state
  useEffect(() => {
    if (!rows.length || originalUnfilteredRows.length || hasFilters) return;
    setOriginalUnfilteredRows(rows);
  }, [rows, originalUnfilteredRows, hasFilters]);

  // Push course name header columns to the table
  useEffect(() => {
    if (!courses?.length) return;

    setColumns(() => {
      const newColumns: any = [];

      courses.forEach(({ _id, title }: CourseName) => {
        if (activeDeselectedCourses.includes(_id)) return;

        newColumns.push({
          Header: title,
          accessor: _id as any,
          sortType: (rowA: Row<Learner>, rowB: Row<Learner>, columnId: string) => {
            const valueA = rowA.original.courseProgress[columnId];
            const valueB = rowB.original.courseProgress[columnId];

            // Sort 'N/A' (undefined) values at the end
            if (!valueA && valueA !== 0) return -1;
            if (!valueB && valueB !== 0) return 1;

            // Sort percentages in correct order
            if (valueA < valueB) return -1;
            if (valueA > valueB) return 1;

            return 0;
          },
          Cell: ({ row: { original } }: CellProps<Learner>) => {
            return renderPercentageCell(original.courseProgress[_id]); // Render the completion percentage cell
          },
          Footer: ({ rows }: any) => {
            // Calculate the average completion percentage for the course, excluding N/A values
            const total = rows.reduce((acc: any, { original }: any) => {
              const courseProgress = original.courseProgress[_id];
              return courseProgress || courseProgress === 0 ? acc + courseProgress : acc;
            }, 0);

            // Get length excluding undefined values
            const rowsLength = rows.filter(({ original }: any) => {
              const courseProgress = original.courseProgress[_id];
              return courseProgress || courseProgress === 0;
            }).length;

            const average = round(total / rowsLength, 2);

            return renderPercentageCell(average, true); // Render the completion percentage cell
          },
        });
      });

      return [...TABLE_COLUMNS, ...newColumns];
    });
  }, [courses, activeDeselectedCourses]);

  // Filter the table based on the learner filter
  useEffect(() => {
    switch (learnerFilter) {
      case LearnerFilter.HaventStarted:
        setFilter(TABLE_COLUMN_ACCESSORS.TOTAL_PERCENTAGE_COMPLETE, '0');
        break;
      case LearnerFilter.HaventCompleted:
        setFilter(TABLE_COLUMN_ACCESSORS.TOTAL_PERCENTAGE_COMPLETE, 'between');
        break;
      case LearnerFilter.Completed:
        setFilter(TABLE_COLUMN_ACCESSORS.TOTAL_PERCENTAGE_COMPLETE, '100');
        break;
      default:
        setFilter(TABLE_COLUMN_ACCESSORS.TOTAL_PERCENTAGE_COMPLETE, undefined);
        break;
    }
  }, [learnerFilter, setFilter]);

  // ===========================================================================
  // Render
  // ===========================================================================

  if (!activeTeams.length) {
    return <p>Please select at least one team to view the report.</p>;
  }

  if (noCoursesSelected) {
    return <p>Please select at least one course to view the report.</p>;
  }

  if (!formattedLearners.length && !coursesIsLoadingOrFetching) {
    return <p>No data available</p>;
  }

  return (
    <LoadingErrorBoundaryRTK
      error={error as FetchBaseQueryError}
      isLoading={isLoading || coursesIsLoadingOrFetching}
    >
      <div className="TableSummaryReport">
        <div className="TableSummaryReport_Top">
          <TilesSummaryReport
            learnerFilter={learnerFilter}
            totalLearnerCount={totalLearnerCount}
            learnersWhoHaventStartedCount={learnersWhoHaventStartedCount}
            learnersWhoHaventCompletedCount={learnersWhoHaventCompletedCount}
            learnersWhoHaveCompletedCount={learnersWhoHaveCompletedCount}
            onCopyClick={handleCopyClick}
            onFilterClick={handleFilterClick}
            onFilterClear={handleClearFilter}
          />

          <div className="TableSummaryReport_Top-Search-Button">
            <DownloadSummaryReportButton
              learnerData={formattedLearners}
              courseData={courses}
              hasSingleSelectedCourse={hasSingleSelectedCourse}
              hasFilters={hasFilters}
              filteredRows={rows}
              learnerFilter={learnerFilter}
              searchQuery={searchQuery}
            />

            <SearchInput
              placeholder="Search by name or email"
              value={searchQuery}
              active={!!searchQuery}
              onChange={handleSearch}
              onClear={handleClearSearchFilter}
              onSearch={() => null}
            />
          </div>
        </div>

        <div
          className="TableSummaryReport_TableContainer"
          ref={containerRef}
          onScroll={handleTableScroll}
        >
          <Table {...getTableProps()}>
            {/* Headers */}
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const columnId = column.id;
                    const isNameColumn = columnId === 'name';

                    return (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(customSortByToggle(column)),
                        )}
                        style={TABLE_HEADER_STYLES(isNameColumn)}
                      >
                        <div>
                          {column.render('Header')}

                          <span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <IconMaterial name="arrow_downward" />
                              ) : (
                                <IconMaterial name="arrow_upward" />
                              )
                            ) : (
                              <IconMaterial name="unfold_more" />
                            )}
                          </span>
                        </div>
                        {/* Zero-height sizer for column-width stabilization (see useColumnWidths). */}
                        {widestValues[columnId] != null && (
                          <div
                            aria-hidden="true"
                            style={{
                              height: 0,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {widestValues[columnId]}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            {/* Learner Data */}
            <tbody {...getTableBodyProps()}>
              {topPadding > 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ height: topPadding, padding: 0, border: 'none' }}
                  />
                </tr>
              )}
              {visibleRows.map((row) => {
                prepareRow(row);

                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, cellIndex) => {
                      const isNameColumn = cell.column.id === 'name';

                      return (
                        <td
                          {...cell.getCellProps()}
                          style={TABLE_CELL_STYLES(isNameColumn, cellIndex)}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {bottomPadding > 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ height: bottomPadding, padding: 0, border: 'none' }}
                  />
                </tr>
              )}
            </tbody>

            {/* Average Row */}
            <tfoot>
              {footerGroups.map((group) => (
                <tr {...group.getFooterGroupProps()}>
                  {group.headers.map((column) => {
                    const isNameColumn = column.id === 'name';

                    return (
                      <td
                        {...column.getFooterProps()}
                        style={TABLE_AVERAGE_CELL_STYLES(isNameColumn)}
                      >
                        {isNameColumn ? 'Average' : column.render('Footer')}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          </Table>
        </div>
      </div>
    </LoadingErrorBoundaryRTK>
  );
};

export default TableSummaryReport;
